import { useEffect, useRef, useState } from 'react';
import type { PastelColor } from '../c2/Pill';

interface Scenario {
  slug: string;
  title: string;
  url: string;
  description: string;
  color: PastelColor;
  icon: string;
}

const SCENARIOS: Record<string, Scenario> = {
  roadmap: {
    slug: 'roadmap',
    title: 'Q4 Product Roadmap',
    url: 'acme.notion.site/Q4-2026-Roadmap',
    description: 'The quarterly plan in Notion',
    color: 'sky',
    icon: '🗺️',
  },
  vacation: {
    slug: 'vacation',
    title: 'Request Time Off',
    url: 'app.rippling.com/time-off/request',
    description: 'Submit a vacation request',
    color: 'peach',
    icon: '🏖️',
  },
  oncall: {
    slug: 'oncall',
    title: 'On-call Runbook',
    url: 'acme.atlassian.net/wiki/On-Call-Runbook',
    description: 'What to do when things break',
    color: 'lemon',
    icon: '🚨',
  },
  newhire: {
    slug: 'newhire',
    title: 'New Hire Onboarding',
    url: 'docs.google.com/document/d/newhire',
    description: 'First-week checklist',
    color: 'mint',
    icon: '🌱',
  },
};

const SCENARIO_KEYS = Object.keys(SCENARIOS);

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

const CYCLE_MS = 9000 / 1.5; // speed=1.5x per canonical EDITMODE

interface BrowserFrameProps {
  label: string;
  dimmed?: boolean;
  children: React.ReactNode;
}

function BrowserFrame({ label, dimmed, children }: BrowserFrameProps) {
  return (
    <div
      className="bg-surface relative transition-opacity duration-[400ms] ease-out"
      style={{
        border: '1px solid var(--color-hairline)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-md)',
        opacity: dimmed ? 0.4 : 1,
        overflow: 'hidden',
        minHeight: 220,
      }}
    >
      <div
        className="flex items-center gap-2"
        style={{
          padding: '8px 12px',
          background: 'var(--color-bg-alt)',
          borderBottom: '1px solid var(--color-hairline)',
        }}
      >
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'oklch(0.72 0.17 25)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'oklch(0.85 0.16 85)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'oklch(0.76 0.16 145)' }} />
        <span className="text-[10.5px] text-ink-muted ml-2 font-medium">{label}</span>
      </div>
      {children}
    </div>
  );
}

interface AddressBarProps {
  value: string;
  phase: Phase;
  flashIntensity: number;
}

function AddressBar({ value, phase, flashIntensity }: AddressBarProps) {
  return (
    <div
      className="m-3"
      style={{
        background: 'var(--color-bg-alt)',
        border: `1.5px solid ${flashIntensity > 0
          ? `color-mix(in oklch, var(--accent-solid) ${Math.round(flashIntensity * 100)}%, var(--color-hairline-strong))`
          : 'var(--color-hairline-strong)'}`,
        borderRadius: 999,
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        transition: 'border-color 80ms ease',
        boxShadow: flashIntensity > 0
          ? `0 0 0 ${Math.round(flashIntensity * 6)}px color-mix(in oklch, var(--accent-soft) 60%, transparent)`
          : 'none',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-ink-muted)" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      <span className="font-mono text-sm text-ink font-semibold">
        {value || <span className="text-ink-muted font-normal">type a go/ link…</span>}
      </span>
      {(phase === 'typing' || (phase === 'idle' && !value)) && (
        <span
          className="inline-block ml-px"
          style={{
            width: 1.5,
            height: 14,
            background: 'var(--accent-deep)',
            animation: 'c2blink 1s step-end infinite',
          }}
        />
      )}
    </div>
  );
}

function DestinationContent({ scenario }: { scenario: Scenario }) {
  if (scenario.slug === 'roadmap') return <NotionDestination />;
  if (scenario.slug === 'vacation') return <RipplingDestination />;
  if (scenario.slug === 'oncall') return <ConfluenceDestination />;
  if (scenario.slug === 'newhire') return <GoogleDocDestination />;
  return null;
}

// ============================================================
// Notion-style roadmap page
// ============================================================
function NotionDestination() {
  const rows = [
    { emoji: '🟢', name: 'Shipping infrastructure', status: 'On track', statusColor: 'mint' as PastelColor },
    { emoji: '🟡', name: 'Search ranking v2', status: 'At risk', statusColor: 'lemon' as PastelColor },
    { emoji: '🟢', name: 'Mobile onboarding', status: 'On track', statusColor: 'mint' as PastelColor },
    { emoji: '🔵', name: 'Enterprise SSO', status: 'Planned', statusColor: 'sky' as PastelColor },
  ];
  return (
    <div>
      <div style={{ height: 68, background: 'linear-gradient(120deg, var(--color-sky) 0%, var(--color-lilac) 100%)' }} />
      <div style={{ padding: '0 22px 18px' }}>
        <div
          className="flex items-center justify-center bg-surface"
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            boxShadow: '0 0 0 3px var(--color-surface), 0 4px 10px rgba(0,0,0,0.06)',
            fontSize: 26,
            marginTop: -24,
            marginBottom: 10,
          }}
        >
          🗺️
        </div>
        <div
          className="font-display font-extrabold text-ink"
          style={{ fontSize: 22, letterSpacing: '-0.01em', lineHeight: 1.15 }}
        >
          Q4 2026 Product Roadmap
        </div>
        <div
          className="flex text-ink-muted mt-1.5"
          style={{ fontSize: 11, gap: 12 }}
        >
          <span>📅 Last edited 2h ago</span>
          <span>·</span>
          <span>👥 12 collaborators</span>
        </div>
        <div
          className="overflow-hidden mt-3.5"
          style={{ border: '1px solid var(--color-hairline)', borderRadius: 6 }}
        >
          <div
            className="grid bg-bg-alt font-semibold uppercase"
            style={{
              gridTemplateColumns: '1fr 60px',
              padding: '6px 10px',
              fontSize: 10,
              color: 'var(--color-ink-muted)',
              letterSpacing: '0.04em',
            }}
          >
            <span>Initiative</span>
            <span>Status</span>
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              className="grid items-center text-ink"
              style={{
                gridTemplateColumns: '1fr 60px',
                padding: '7px 10px',
                fontSize: 11,
                borderTop: '1px solid var(--color-hairline)',
              }}
            >
              <span className="flex items-center gap-1.5">
                <span style={{ fontSize: 9 }}>{r.emoji}</span>
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{r.name}</span>
              </span>
              <span
                className="inline-block text-center font-semibold"
                style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: pastelBg[r.statusColor],
                  color: pastelDeep[r.statusColor],
                  fontSize: 9,
                }}
              >
                {r.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Rippling-style time-off request form
// ============================================================
function RipplingDestination() {
  return (
    <div style={{ padding: '18px 22px 22px' }}>
      <div
        className="flex items-center font-medium uppercase text-ink-muted"
        style={{ gap: 8, marginBottom: 4, fontSize: 10, letterSpacing: '0.06em' }}
      >
        <span>Rippling</span><span>·</span><span>Time Off</span>
      </div>
      <div
        className="font-display font-extrabold text-ink"
        style={{ fontSize: 20, letterSpacing: '-0.01em', marginBottom: 14 }}
      >
        Request time off 🏖️
      </div>
      <div className="flex flex-col" style={{ gap: 10 }}>
        <RipplingFormField label="Policy" value="Paid time off (PTO)" badge="12 days left" />
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <RipplingFormField label="From" value="Mon · Dec 22" />
          <RipplingFormField label="To" value="Fri · Dec 26" />
        </div>
        <div
          className="flex justify-between items-center font-semibold"
          style={{
            padding: '10px 12px',
            background: 'var(--accent-soft)',
            borderRadius: 8,
            fontSize: 11,
            color: 'var(--accent-deep)',
          }}
        >
          <span>5 business days · 40 hours</span>
          <span
            style={{
              padding: '4px 10px',
              background: 'var(--accent-solid)',
              color: '#fff',
              borderRadius: 999,
              fontSize: 10,
            }}
          >
            Submit request
          </span>
        </div>
      </div>
    </div>
  );
}

function RipplingFormField({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <div>
      <div
        className="font-semibold uppercase text-ink-muted"
        style={{ fontSize: 9, letterSpacing: '0.04em', marginBottom: 4 }}
      >
        {label}
      </div>
      <div
        className="bg-surface flex items-center justify-between text-ink"
        style={{
          padding: '8px 10px',
          border: '1px solid var(--color-hairline)',
          borderRadius: 6,
          fontSize: 11,
          gap: 6,
        }}
      >
        <span className="font-semibold">{value}</span>
        {badge && (
          <span
            className="font-semibold"
            style={{ fontSize: 9, color: 'var(--accent-deep)' }}
          >
            {badge}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Confluence-style on-call runbook
// ============================================================
function ConfluenceDestination() {
  const steps = [
    { n: 1, title: 'Acknowledge the alert', done: true, mono: false },
    { n: 2, title: 'Check #ops-incidents in Slack', done: true, mono: false },
    { n: 3, title: 'Open Grafana: api-gateway dashboard', done: false, mono: false },
    { n: 4, title: 'Run `kubectl get pods -n prod`', done: false, mono: true },
  ];
  return (
    <div style={{ padding: '14px 20px 18px' }}>
      <div
        className="flex items-center text-ink-muted"
        style={{ gap: 8, fontSize: 10, marginBottom: 4 }}
      >
        <span>Engineering</span><span>›</span><span>Runbooks</span>
      </div>
      <div
        className="font-display font-extrabold text-ink flex items-center"
        style={{ fontSize: 20, letterSpacing: '-0.01em', marginBottom: 10, gap: 8 }}
      >
        <span>🚨</span><span>On-call Runbook</span>
      </div>
      <div
        className="font-semibold mb-3"
        style={{
          padding: '8px 10px',
          borderLeft: '3px solid var(--color-peach-deep)',
          background: 'var(--color-peach)',
          borderRadius: '0 4px 4px 0',
          fontSize: 10,
          color: 'var(--color-peach-deep)',
        }}
      >
        ⚡ If PagerDuty is paging you, start at step 1.
      </div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {steps.map(s => (
          <div
            key={s.n}
            className="flex items-start"
            style={{
              gap: 8,
              fontSize: 11,
              color: s.done ? 'var(--color-ink-faint)' : 'var(--color-ink)',
              lineHeight: 1.4,
            }}
          >
            <span
              className="rounded flex-shrink-0 flex items-center justify-center font-bold"
              style={{
                width: 16,
                height: 16,
                border: `1.5px solid ${s.done ? 'var(--color-mint-deep)' : 'var(--color-hairline-strong)'}`,
                background: s.done ? 'var(--color-mint-deep)' : 'transparent',
                color: '#fff',
                fontSize: 9,
              }}
            >
              {s.done ? '✓' : s.n}
            </span>
            <span
              style={{
                fontFamily: s.mono ? '"JetBrains Mono", ui-monospace, monospace' : 'inherit',
                textDecoration: s.done ? 'line-through' : 'none',
                background: s.mono ? 'var(--color-bg-alt)' : 'transparent',
                padding: s.mono ? '1px 5px' : 0,
                borderRadius: 3,
              }}
            >
              {s.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Google Doc-style new-hire onboarding
// ============================================================
function GoogleDocDestination() {
  const items = [
    { t: 'Set up your laptop', done: true },
    { t: 'Join #general in Slack', done: true },
    { t: 'Meet your buddy for coffee ☕', done: false },
  ];
  return (
    <div>
      <div
        className="flex items-center bg-surface text-ink-muted"
        style={{
          padding: '6px 16px',
          borderBottom: '1px solid var(--color-hairline)',
          gap: 8,
          fontSize: 10,
        }}
      >
        <span className="font-semibold text-ink">File</span>
        <span>Edit</span><span>View</span><span>Insert</span>
        <span
          className="font-bold ml-auto"
          style={{
            padding: '2px 8px',
            background: 'var(--color-mint)',
            color: 'var(--color-mint-deep)',
            borderRadius: 999,
            fontSize: 9,
          }}
        >
          Share
        </span>
      </div>
      <div style={{ padding: '18px 22px 20px' }}>
        <div
          className="font-display font-extrabold text-ink"
          style={{ fontSize: 22, letterSpacing: '-0.01em', marginBottom: 4 }}
        >
          Welcome to Acme! 🌱
        </div>
        <div className="text-ink-muted mb-4" style={{ fontSize: 11 }}>
          Your first-week checklist · Updated by Priya S.
        </div>
        <div
          className="font-display font-bold text-ink mb-2"
          style={{ fontSize: 12 }}
        >
          Day 1
        </div>
        <div className="flex flex-col mb-3" style={{ gap: 6 }}>
          {items.map((it, i) => (
            <div
              key={i}
              className="flex items-center"
              style={{
                gap: 7,
                fontSize: 11,
                color: it.done ? 'var(--color-ink-faint)' : 'var(--color-ink)',
              }}
            >
              <span
                className="flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: 3,
                  border: `1.5px solid ${it.done ? 'var(--color-mint-deep)' : 'var(--color-hairline-strong)'}`,
                  background: it.done ? 'var(--color-mint-deep)' : 'transparent',
                  color: '#fff',
                  fontSize: 8,
                }}
              >
                {it.done ? '✓' : ''}
              </span>
              <span style={{ textDecoration: it.done ? 'line-through' : 'none' }}>{it.t}</span>
            </div>
          ))}
        </div>
        <div
          className="font-display font-bold text-ink mb-2"
          style={{ fontSize: 12 }}
        >
          First week
        </div>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div style={{ height: 5, borderRadius: 3, background: 'var(--color-bg-alt)', width: '92%' }} />
          <div style={{ height: 5, borderRadius: 3, background: 'var(--color-bg-alt)', width: '78%' }} />
          <div style={{ height: 5, borderRadius: 3, background: 'var(--color-bg-alt)', width: '65%' }} />
        </div>
      </div>
    </div>
  );
}

interface FlightPathProps {
  progress: number;
  active: boolean;
}

// 180×100 container with a parabolic arc, dashed path, start/end nodes, a blue
// dot that travels along the arc when active, and a 'go/' pill label centered
// above the arc.
function FlightPath({ progress, active }: FlightPathProps) {
  const width = 180;
  const height = 100;
  const x = progress * width;
  const y = height / 2 - Math.sin(progress * Math.PI) * (height / 2 - 10);

  return (
    <div style={{ width, height, position: 'relative' }}>
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
      >
        {/* Dashed arc */}
        <path
          d={`M 0 ${height / 2} Q ${width / 2} 10 ${width} ${height / 2}`}
          stroke={active ? 'var(--accent-solid)' : 'var(--color-hairline-strong)'}
          strokeWidth="2"
          strokeDasharray="5 4"
          fill="none"
          opacity={active ? 0.6 : 0.3}
        />
        {/* Start + end nodes */}
        <circle
          cx={0}
          cy={height / 2}
          r="5"
          fill={active ? 'var(--accent-solid)' : 'var(--color-hairline-strong)'}
        />
        <circle
          cx={width}
          cy={height / 2}
          r="5"
          fill={active && progress > 0.9 ? 'var(--accent-solid)' : 'var(--color-hairline-strong)'}
        />

        {/* Blue dot traveling along the arc */}
        {active && progress > 0 && progress < 1 && (
          <circle cx={x} cy={y} r="5" fill="var(--accent-solid)" />
        )}

        {/* go/ pill label centered above the arc */}
        <g transform={`translate(${width / 2}, 28)`}>
          <rect
            x={-28}
            y={-12}
            width="56"
            height="22"
            rx="11"
            fill="var(--color-surface)"
            stroke={active ? 'var(--accent-solid)' : 'var(--color-hairline-strong)'}
            strokeWidth="1.5"
          />
          <text
            x="0"
            y="4"
            textAnchor="middle"
            fontSize="12"
            fontFamily='"JetBrains Mono", ui-monospace, monospace'
            fontWeight="600"
            fill={active ? 'var(--accent-deep)' : 'var(--color-ink-soft)'}
          >
            go/
          </text>
        </g>
      </svg>
    </div>
  );
}

type Phase = 'idle' | 'typing' | 'flash' | 'travel' | 'arrived';

export default function DemoHero() {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const scenario = SCENARIOS[SCENARIO_KEYS[currentIdx]!]!;

  useEffect(() => {
    if (!playing) return;
    const loop = (now: number) => {
      if (startRef.current == null) startRef.current = now;
      const elapsed = now - startRef.current;
      if (elapsed >= CYCLE_MS) {
        startRef.current = now;
        setCurrentIdx(idx => (idx + 1) % SCENARIO_KEYS.length);
        setT(0);
      } else {
        setT(elapsed / CYCLE_MS);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [playing]);

  // Phase mapping (per design):
  // 0–0.13 idle | 0.13–0.37 typing | 0.37–0.44 flash | 0.44–0.55 travel | 0.55+ arrived
  const phase: Phase =
    t < 0.13 ? 'idle' :
    t < 0.37 ? 'typing' :
    t < 0.44 ? 'flash' :
    t < 0.55 ? 'travel' :
    'arrived';

  const typedText = (() => {
    if (t < 0.13) return '';
    if (t >= 0.37) return 'go/' + scenario.slug;
    const typeProgress = Math.min(1, (t - 0.13) / 0.24);
    const full = 'go/' + scenario.slug;
    return full.slice(0, Math.round(full.length * typeProgress));
  })();

  const travelProgress = t < 0.44 ? 0 : t < 0.55 ? (t - 0.44) / 0.11 : 1;
  const arrivedProgress = t < 0.55 ? 0 : Math.min(1, (t - 0.55) / 0.1);
  const flashIntensity = t >= 0.37 && t < 0.44 ? 1 - (t - 0.37) / 0.07 : 0;

  return (
    <div className="relative w-full">
      <div
        className="relative grid items-center mx-auto"
        style={{ gridTemplateColumns: '1fr auto 1fr', gap: 24, maxWidth: 1100 }}
      >
        <BrowserFrame label="Your browser" dimmed={phase === 'arrived'}>
          <AddressBar value={typedText} phase={phase} flashIntensity={flashIntensity} />
          <div
            className="text-center italic"
            style={{ padding: '60px 20px', color: 'var(--color-ink-muted)', fontSize: 13 }}
          >
            {phase === 'idle' && 'Type go/ and a short name...'}
            {phase === 'typing' && 'Typing...'}
            {(phase === 'flash' || phase === 'travel') && 'Resolving your go/ link...'}
            {phase === 'arrived' && '(Redirecting...)'}
          </div>
        </BrowserFrame>

        <FlightPath progress={travelProgress} active={phase === 'travel' || phase === 'flash' || phase === 'arrived'} />

        <div
          className="relative"
          style={{
            opacity: arrivedProgress,
            transform: `translateY(${(1 - arrivedProgress) * 20}px) scale(${0.95 + arrivedProgress * 0.05})`,
          }}
        >
          <BrowserFrame label="Destination">
            <div
              className="flex items-center gap-2.5 font-mono text-xs text-ink-muted"
              style={{
                padding: '10px 16px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--color-surface-alt)',
              }}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-solid)' }} />
              <span className="overflow-hidden text-ellipsis whitespace-nowrap">{scenario.url}</span>
            </div>
            <DestinationContent scenario={scenario} />
          </BrowserFrame>
        </div>
      </div>

      <div className="text-center mt-9">
        <button
          onClick={() => { setPlaying(p => !p); if (!playing) startRef.current = null; }}
          className="inline-flex items-center gap-2 cursor-pointer font-semibold"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-hairline-strong)',
            padding: '9px 18px',
            borderRadius: 999,
            fontSize: 13,
            color: 'var(--color-ink)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {playing ? '⏸ Pause' : '▶ Play'}
        </button>
      </div>

      <style>{`@keyframes c2blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
    </div>
  );
}
